import {useSelector} from 'react-redux';
import {RootState} from '../../redux/store';
import {Dispatch, SetStateAction, useCallback, useState} from 'react';
import {
  uploadEvidenceSource,
  downloadTemplateManifest,
  deleteEvidenceSource,
  checkEvidenceDuplicateName,
  getEvidenceSourceSessionId,
} from '../../api/EvidenceSourceUpload';
import {useToast} from '../../contexts/toastContext/ToastContext';
import {t} from 'i18next';
import {AxiosError} from 'axios';
import {showErrorToast} from '../../api/utils';
import {
  DeleteFileType,
  DuplicateNameResponse,
  ManifestAndPassKey,
} from '../../types/upload-new-eviidence/evidenceSourceTypes';

export const MAX_PASSKEY_UPLOADS = 10;

export interface UseEvidenceSourceReturn {
  isManifestUploading: boolean;
  isPasskeyUploading: boolean;
  manifestUploadedData?: ManifestAndPassKey;
  passkeysUploadedData?: ManifestAndPassKey[];
  isManifestError?: string;
  isPasskeyError?: string;
  downloadManifestTemplate: () => void;
  uploadManifestFile: (files?: File[]) => void;
  uploadPasskeyFile: (files?: File[]) => void;
  updateManifest: (data: any) => void;
  updatePasskey: (data: any) => void;
  deleteManifestFile: (fileId: string) => void;
  deletePasskeyFile: (fileId: string) => void;
  setIsManifestError: Dispatch<SetStateAction<string | undefined>>;
  setIsPasskeyError: Dispatch<SetStateAction<string | undefined>>;
  checkEvidenceName: (evidenceName: string) => Promise<DuplicateNameResponse>;
  getEvidenceSourceId: () => Promise<string>;
}

export const useEvidenceSource = (): UseEvidenceSourceReturn => {
  const clientId = useSelector(
    (state: RootState) => state.selectedClient
  ).clientId;

  const projectId = useSelector(
    (state: RootState) =>
      state.uploadEvidenceSource.selectedProject?.projectId ?? ''
  );

  const [isManifestUploading, setIsManifestUploading] =
    useState<boolean>(false);
  const [isPasskeyUploading, setIsPasskeyUploading] = useState<boolean>(false);
  const [manifestUploadedData, setManifestUploadedData] =
    useState<ManifestAndPassKey>();
  const [passkeysUploadedData, setPasskeysUploadedData] =
    useState<ManifestAndPassKey[]>();
  const [isManifestError, setIsManifestError] = useState<string>();
  const [isPasskeyError, setIsPasskeyError] = useState<string>();

  const {showToast} = useToast();
  const {evidenceSourceSessionId} = useSelector(
    (state: RootState) => state.uploadEvidenceSource
  );

  const downloadManifestTemplate = useCallback(() => {
    downloadTemplateManifest(clientId, projectId)
      .then((response) => {
        const url = globalThis.URL.createObjectURL(new Blob([response.data]));
        const childNode = document.createElement('a');
        childNode.style.display = 'none';
        childNode.href = url;
        childNode.download = `Template.xlsx`;
        document.body.appendChild(childNode);
        childNode.click();
        globalThis.URL.revokeObjectURL(url);
        childNode.remove();
      })
      .catch(() => {
        showToast(
          t('essentialProcessingInstructions.documentDownloadFailed'),
          'error',
          undefined,
          'center'
        );
      });
  }, [clientId, projectId, showToast]);

  const uploadManifestFile = useCallback(
    (files?: File[]) => {
      const file = files?.[0];
      if (file) {
        setIsManifestUploading(true);
        uploadEvidenceSource(
          'manifest',
          clientId,
          projectId,
          file,
          evidenceSourceSessionId ?? ''
        )
          .then((response: any) => {
            const data = response.data.data;
            setManifestUploadedData({
              ...data,
              fileName: data?.path?.split(/[/\\]/).pop() || '',
              fileSizeInBytes: file.size,
            });
          })
          .catch((error) => {
            const validationErrors = error.response.data?.validationErrors;
            if (validationErrors?.[0]?.field === 'file') {
              const message = validationErrors?.[0]?.message?.[0];
              setIsManifestError(message);
            } else {
              showErrorToast(showToast, error, 'center');
            }
          })
          .finally(() => {
            setIsManifestUploading(false);
          });
      }
    },
    [clientId, projectId, evidenceSourceSessionId, showToast]
  );

  const uploadPasskey = useCallback(
    (file: File) => {
      return new Promise((resolve) => {
        uploadEvidenceSource(
          'passkey',
          clientId,
          projectId,
          file,
          evidenceSourceSessionId ?? ''
        )
          .then((response: any) => {
            const data = response.data.data;
            setPasskeysUploadedData((prevData) => [
              ...(prevData ?? []),
              {
                ...data,
                fileName: data?.path?.split(/[/\\]/).pop() || '',
                fileSizeInBytes: file.size,
              },
            ]);
          })
          .catch((error) => {
            const validationErrors = error.response.data?.validationErrors;
            if (validationErrors?.[0]?.field === 'file') {
              const message = validationErrors?.[0]?.message?.[0];
              setIsPasskeyError(message);
            } else {
              showErrorToast(showToast, error, 'center');
            }
          })
          .finally(() => {
            resolve(true);
          });
      });
    },
    [clientId, evidenceSourceSessionId, projectId, showToast]
  );

  const uploadPasskeyFile = useCallback(
    async (files?: File[]) => {
      if (files) {
        setIsPasskeyUploading(true);
        const maxAllowedSize = 2097152; // Max 2MB;
        try {
          let count = passkeysUploadedData?.length ?? 0;
          for (const file of files) {
            if (count < MAX_PASSKEY_UPLOADS) {
              if (file.size > maxAllowedSize) {
                setIsPasskeyError(
                  t('essentialProcessingInstructions.passkeySizeError')
                );
              } else {
                await uploadPasskey(file);
                count++;
              }
            } else {
              break;
            }
          }
        } finally {
          setIsPasskeyUploading(false);
        }
      }
    },
    [uploadPasskey, passkeysUploadedData]
  );

  const deleteManifestFile = useCallback(
    (fileId: string) => {
      setIsManifestUploading(true);

      deleteEvidenceSource(DeleteFileType.Manifest, clientId, projectId, fileId)
        .then(() => {
          setManifestUploadedData(undefined);
        })
        .catch((error) => {
          const err = error as AxiosError;
          showErrorToast(showToast, err, 'center');
        })
        .finally(() => {
          setIsManifestUploading(false);
        });
    },
    [clientId, projectId, showToast]
  );

  const deletePasskeyFile = useCallback(
    (fileId: string) => {
      setIsPasskeyUploading(true);

      deleteEvidenceSource(DeleteFileType.Passkey, clientId, projectId, fileId)
        .then(() => {
          setPasskeysUploadedData(
            passkeysUploadedData?.filter((item) => item.fileId !== fileId)
          );
        })
        .catch((error) => {
          const err = error as AxiosError;
          showErrorToast(showToast, err, 'center');
        })
        .finally(() => {
          setIsPasskeyUploading(false);
        });
    },
    [clientId, passkeysUploadedData, projectId, showToast]
  );

  const checkEvidenceName = useCallback(
    async (evidenceName: string): Promise<DuplicateNameResponse> => {
      const response = await checkEvidenceDuplicateName(
        clientId,
        projectId,
        evidenceName
      );
      return response.data;
    },
    [clientId, projectId]
  );

  const getEvidenceSourceId = useCallback(async (): Promise<string> => {
    const response = await getEvidenceSourceSessionId();
    return response.data?.evidenceSourceSessionId;
  }, []);

  const updateManifest = useCallback((data: any) => {
    setManifestUploadedData(data);
  }, []);

  const updatePasskey = useCallback((data: any) => {
    setPasskeysUploadedData(data);
  }, []);

  return {
    isManifestUploading,
    isPasskeyUploading,
    manifestUploadedData,
    passkeysUploadedData,
    isManifestError,
    isPasskeyError,
    downloadManifestTemplate,
    uploadManifestFile,
    uploadPasskeyFile,
    updateManifest,
    updatePasskey,
    deleteManifestFile,
    deletePasskeyFile,
    setIsManifestError,
    setIsPasskeyError,
    checkEvidenceName,
    getEvidenceSourceId,
  };
};
